/**
 * Detail view for a single M List entry. Modal layout: left content, right image, CTA.
 * Uses existing listing fields; no backend changes. Twentytwentysix design language.
 */
import React, { useEffect } from "react";

function getTagLabel(t, tagIdToTag = {}) {
  if (t && typeof t === "object" && (t.name || t.slug)) return { key: t.id ?? t.slug ?? t.name, label: t.name ?? t.slug };
  const resolved = tagIdToTag[t];
  if (resolved) return { key: resolved.id, label: resolved.name ?? resolved.slug };
  return null;
}

export default function MListListingDetailModal({ entry, tagIdToTag = {}, onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!entry) return null;

  const name = entry.business_name ?? "Listing";
  const tagLabels = (entry.tags || []).map((t) => getTagLabel(t, tagIdToTag)).filter(Boolean);
  const imageUrl = entry.graphic_800 || entry.graphic_400;
  const ctaUrl = entry.website?.startsWith("http") ? entry.website : entry.website ? `https://${entry.website}` : null;

  return (
    <div
      className="twentytwentysix-content__listing-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="listing-detail-title"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="twentytwentysix-content__listing-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="twentytwentysix-content__listing-detail-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="twentytwentysix-content__listing-detail-grid">
          <div className="twentytwentysix-content__listing-detail-content">
            {tagLabels.length > 0 && (
              <p className="twentytwentysix-content__listing-detail-meta">
                {tagLabels.map(({ label }) => label).join(" · ")}
              </p>
            )}
            <h2 id="listing-detail-title" className="twentytwentysix-content__listing-detail-title">
              {name}
            </h2>
            {entry.short_description && (
              <p className="twentytwentysix-content__listing-detail-subhead">{entry.short_description}</p>
            )}
            {entry.extended_description && (
              <div className="twentytwentysix-content__listing-detail-body">
                {entry.extended_description}
              </div>
            )}
            {(entry.website || entry.instagram_name || entry.facebook_url || entry.email || entry.phone) && (
              <div className="twentytwentysix-content__listing-detail-links">
                {entry.website && (
                  <a href={ctaUrl || entry.website} target="_blank" rel="noopener noreferrer" className="twentytwentysix-content__link">
                    Website
                  </a>
                )}
                {entry.instagram_name && (
                  <a href={`https://instagram.com/${entry.instagram_name.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="twentytwentysix-content__link">
                    Instagram
                  </a>
                )}
                {entry.facebook_url && (
                  <a href={entry.facebook_url.startsWith("http") ? entry.facebook_url : `https://${entry.facebook_url}`} target="_blank" rel="noopener noreferrer" className="twentytwentysix-content__link">
                    Facebook
                  </a>
                )}
                {entry.email && (
                  <a href={`mailto:${entry.email}`} className="twentytwentysix-content__link">Email</a>
                )}
                {entry.phone && (
                  <a href={`tel:${entry.phone}`} className="twentytwentysix-content__link">Phone</a>
                )}
              </div>
            )}
            {tagLabels.length > 0 && (
              <div className="twentytwentysix-content__listing-detail-badges">
                {tagLabels.map(({ key, label }) => (
                  <span key={key} className="twentytwentysix-content__listing-tag">
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="twentytwentysix-content__listing-detail-image-wrap">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="twentytwentysix-content__listing-detail-image" />
            ) : (
              <div className="twentytwentysix-content__listing-detail-image-placeholder" aria-hidden="true" />
            )}
          </div>
        </div>

        <div className="twentytwentysix-content__listing-detail-actions">
          {ctaUrl && (
            <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="twentytwentysix-content__pill">
              Visit website
            </a>
          )}
          <button type="button" className="twentytwentysix-content__pill twentytwentysix-content__pill--outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
